import { Inject, Injectable, InjectionToken } from '@angular/core';
import { ValidationResult } from '../../validation/interface/validation.interface';
import { OrderRoutePathConfig } from '../interface/order.interface';

export const TREE_NODE_VALUE = new InjectionToken<number>('TreeNodeValue');

@Injectable({
  providedIn: 'root',
})
export class RouteTreeNode<T> {
  value: T;
  parent: RouteTreeNode<T> | null;
  children: RouteTreeNode<T>[];
  validation: ValidationResult[] = [];

  constructor(@Inject(TREE_NODE_VALUE) value: T, parent: RouteTreeNode<T> | null = null) {
    this.value = value;
    this.parent = parent;
    this.children = [];
  }

  addChild(childNode: T): RouteTreeNode<T> {
    const child = new RouteTreeNode(childNode, this);
    this.children.push(child);
    return child;
  }

  /** DFS to flatten the tree into an array of nodes */
  flattenDFS(): RouteTreeNode<T>[] {
    let nodes: RouteTreeNode<T>[] = [this]; // Start with the current node
    for (const child of this.children) {
      nodes = nodes.concat(child.flattenDFS());
    }
    return nodes;
  }

  /*
  Depth-First Search (DFS) to Find the route node
  DFS is a common algorithm for traversing trees. DFS used to find the path between the root and a specific node.
  */
  findRouteDFS(
    node: RouteTreeNode<OrderRoutePathConfig>,
    basePath: string = '',
    targetPath: string,
    targetNode: RouteTreeNode<OrderRoutePathConfig> | null = null
  ): RouteTreeNode<OrderRoutePathConfig> | null {
    const fullPath = `${basePath}/${node.value.route}`.replace(/^\/+/g, '');

    /**If the target path is found, return the node otherwise return null */
    if (fullPath == targetPath) {
      targetNode = node;
      return targetNode;
    }

    /** Recursively search in the children*/
    for (const child of node.children) {
      const result = this.findRouteDFS(child, fullPath, targetPath, targetNode);
      if (result) {
        targetNode = result;
        return targetNode;
      }
    }
    return null;
  }

  /** function used to find previous and next route when given a specific path
   * step 1 : flatten the route nodes (flattenDFS)
   * step 2 : finding the specific route of the path (findRouteDFS)
   * step 3 : finding previous and next route and path (findLeafRoute,getFullPath )
   */
  findPreviousNextCurrentNode(path: string): {
    previousNode: RouteTreeNode<OrderRoutePathConfig> | null;
    previousPath: string;
    nextNode: RouteTreeNode<OrderRoutePathConfig> | null;
    nextPath: string;
    routeNode: RouteTreeNode<OrderRoutePathConfig> | null;
  } {
    const flattenNodes = this.flattenDFS();
    flattenNodes.shift(); // removing root node

    /**finding the route that match the path through DFS */
    const routeNodeDFS = this.findRouteDFS(this as RouteTreeNode<OrderRoutePathConfig>, '', path, null);
    const routeIndex = flattenNodes.findIndex((node) => routeNodeDFS === node);

    if (routeIndex > -1) {
      /**finding previous route node and  path */
      let prevNode: RouteTreeNode<T> | null = flattenNodes[routeIndex - 1];

      if (prevNode?.children.length) prevNode = this.findLeafRoute(flattenNodes.slice(0, routeIndex).reverse());

      const prevPath = prevNode
        ? this.modifyPath(this.getFullPath(prevNode as RouteTreeNode<OrderRoutePathConfig>, ''))
        : '';

      /*finding next route node and path*/
      let nextNode: RouteTreeNode<T> | null = flattenNodes[routeIndex + 1];

      if (nextNode?.children.length)
        nextNode = this.findLeafRoute(flattenNodes.slice(routeIndex + 1, flattenNodes.length));

      const nextPath = nextNode
        ? this.modifyPath(this.getFullPath(nextNode as RouteTreeNode<OrderRoutePathConfig>, ''))
        : '';

      return {
        previousNode: prevNode as RouteTreeNode<OrderRoutePathConfig>,
        previousPath: prevPath,
        nextNode: nextNode as RouteTreeNode<OrderRoutePathConfig>,
        nextPath: nextPath,
        routeNode: routeNodeDFS,
      };
    } else return { previousNode: null, previousPath: '', nextNode: null, nextPath: '', routeNode: null };
  }

  findNextAvailablePath(path: string) {
    const pathArr = path.split('/');

    for (let i = pathArr.length; i > 0; i--) {
      const concatPath = pathArr
        .slice(0, i)
        ?.join('/')
        ?.replace(/^\/+|\/+$/g, '');

      if (concatPath) {
        const routeInfo = this.findPreviousNextCurrentNode(concatPath);
        if (routeInfo.nextPath) return routeInfo.nextPath;
      }
    }

    return;
  }

  /** function for finding the previous or next leaf route node*/
  findLeafRoute(nodes: RouteTreeNode<T>[]): RouteTreeNode<T> | null {
    for (const node of nodes) {
      if (!node.children.length) {
        return node;
      }
    }
    return null;
  }

  /** function for getting full path of the route node through recursive way*/
  getFullPath(node: RouteTreeNode<OrderRoutePathConfig>, basePath: string = ''): string {
    let fullPath = basePath.concat('/', node.value.route);

    if (node.parent != null) {
      fullPath = this.getFullPath(node.parent, fullPath);
    }
    return fullPath;
  }

  /** function used to modify the path  */
  modifyPath(path: string) {
    const fullPath = path
      .replace(/^\/+|\/+$/g, '')
      .split('/')
      .reverse()
      .join('/');
    return fullPath;
  }

  /**Add validation to route */
  addValidationToRoute(path: string, validationRes: ValidationResult) {
    const routeNodeDFS = this.findRouteDFS(this as RouteTreeNode<OrderRoutePathConfig>, '', path, null);
    if (routeNodeDFS) routeNodeDFS.validation.push(validationRes);
  }

  /**
   * Get validation details of a route
   * It will fetch validations of all routes when there is no path specified
   */
  getValidationDetail(path?: string): {
    hasError: boolean;
    validation: ValidationResult[];
    childrenValidation: ValidationResult[];
    routeNode: RouteTreeNode<OrderRoutePathConfig> | null;
  } {
    const routeNodeDFS = this.findRouteDFS(this as RouteTreeNode<OrderRoutePathConfig>, '', path ?? '', null);

    if (routeNodeDFS) {
      const childVal = this.getChildrenValidation(routeNodeDFS.children);

      return {
        hasError: routeNodeDFS.validation.length || childVal.length ? true : false,
        validation: routeNodeDFS.validation,
        childrenValidation: childVal,
        routeNode: routeNodeDFS,
      };
    } else return { hasError: false, validation: [], childrenValidation: [], routeNode: null };
  }

  /** This function is used to get all the child validations through recursively
   */
  getChildrenValidation(
    node: RouteTreeNode<OrderRoutePathConfig>[],
    validationList: ValidationResult[] = []
  ): ValidationResult[] {
    node?.forEach((n) => {
      if (n?.children?.length) {
        this.getChildrenValidation(n.children, validationList);
      } else {
        n.validation?.forEach((v) => {
          validationList.push(v);
        });
      }
    });

    return validationList;
  }

  /** Removing validation from all routes through recursively*/
  clearValidation() {
    if (this.validation.length) this.validation = [];
    for (const child of this.children) {
      child.clearValidation();
    }
  }

  /** Recursively prune validation items matching predicate. Returns true if anything was removed. */
  removeValidationFromRoute(predicate: (v: ValidationResult) => boolean): boolean {
    const before = this.validation.length;
    this.validation = this.validation.filter((v) => !predicate(v));
    let removed = this.validation.length !== before;
    for (const child of this.children) {
      if (child.removeValidationFromRoute(predicate)) removed = true;
    }
    return removed;
  }
}
